import { Fragment, useState, useEffect } from 'react';
import {
  Dialog, DialogBackdrop, DialogPanel, DialogTitle, Transition, TransitionChild,
} from '@headlessui/react';
import { Info } from '../iconComponents';
import FieldError from '../FieldError/FieldError';

export const DialogBox = ({
  heading, subHeading, isOpen, onCloseHandler, primaryButtonText, secondaryButtonText, onHandleSecondaryClick,
  onHandlePrimaryClick, headingIcon, content, isLaterModal, isError, isMyProfileModal, myProfileTns, infoText = '',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isDeleteAccountConfirmation = false, isCancelPlanConfirmation = false, warningText = '',
  primaryButtonDisabled = false,
}: {
  heading: string, subHeading: string, isOpen: boolean, onCloseHandler?: () => void, primaryButtonText?: string,
  secondaryButtonText?: string, onHandleSecondaryClick?: () => void, onHandlePrimaryClick?: () => void, headingIcon?: JSX.Element,
  content?: JSX.Element, isLaterModal?: boolean, isError?:string, isMyProfileModal?:boolean, myProfileTns?:string,
  infoText?: string, isDeleteAccountConfirmation? : boolean, isCancelPlanConfirmation?: boolean, warningText?: string,
  primaryButtonDisabled?: boolean,
}) => {
  const [open, setOpen] = useState(isOpen);
  const [isConfirmedDelete, setConfirmDelete] = useState('');

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={setOpen} onClick={onCloseHandler} data-cy="dialog-box">
        <DialogBackdrop
          transition
          className={[
            'fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity',
            'data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200',
            'data-[enter]:ease-out data-[leave]:ease-in',
          ].join(' ')}
        />

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex mt-32 justify-center p-4 text-center items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                className={`relative transform overflow-hidden rounded-xl bg-white px-5 pt-5 pb-4 text-left
                shadow-xl transition-all ${isMyProfileModal ? 'w-72' : 'h-70'}  ${isLaterModal ? 'reminder-later-panel' : 'w-72'}`}
                data-cy="dialog-panel"
              >
                <div className="flex items-center mb-3">
                  <div className={`flex items-center delete-icon ${isLaterModal ? 'later-icon' : ' '}`}>
                    { headingIcon && (
                      <div className="dialog-heading-icon flex-shrink-0" data-cy="dialog-heading-icon">
                        {headingIcon}
                      </div>
                    )}
                    <div className="flex flex-col px-2">
                      <DialogTitle as="h3" className={`dialog-title-heading text-left font-semibold ${isLaterModal ? 'dialogbox-heading' : ''}`} data-cy="popup-heading">
                        {heading}
                      </DialogTitle>
                    </div>
                  </div>
                </div>
                <div>
                  <div>
                    <div>
                      <p
                        className={`${isMyProfileModal ? 'text-sm text-gray-600 leading-snug'
                          : 'text-sm text-gray-600 leading-relaxed'} ${isLaterModal ? 'dialogbox-subheading' : ''}`}
                        data-cy="popup-subheading"
                      >
                        {subHeading}
                      </p>
                      {infoText
                        && (
                          <div className="flex justify-start items-center mt-3">
                            <div className="text-gray-400 mr-1 dialog-info-text">
                              <Info />
                            </div>
                            <p className="text-xs text-gray-400">
                              {infoText}
                            </p>
                          </div>
                        )}
                      {warningText
                        && (
                          <div className="flex justify-start items-center mt-3">
                            <div className="text-red-500 mr-1 dialog-info-text">
                              <Info />
                            </div>
                            <p className="text-xs text-red-500">
                              {warningText}
                            </p>
                          </div>
                        )}
                      <span
                        className={`${isMyProfileModal ? 'font-normal text-xs text-justify text-reset-info mt-3 justify-center flex items-center' : ''}`}
                        data-cy="myprofile-tns"
                      >
                        {myProfileTns ? myProfileTns : ''}
                      </span>
                      <div className={`${isLaterModal ? 'content-design' : ''}`} data-cy="popup-content">
                        {content}
                        {isError && <FieldError errorMessage={isError} />}
                      </div>
                    </div>
                    <div className="mt-5 space-y-2">
                      {isDeleteAccountConfirmation && (
                        <>
                          <span
                            className="font-normal text-xs text-justify text-reset-info block"
                            data-cy="delete-text"
                          >
                            Type
                            {' '}
                            <b>DELETE</b>
                            {' '}
                            and click
                            {' '}
                            <b>YES, DELETE</b>
                            {' '}
                            to permanently delete your account.
                          </span>
                          <input
                            name="confirm"
                            type="text"
                            value={isConfirmedDelete}
                            className="block w-full h-10 appearance-none rounded-lg
                        border border-gray-300 px-3 placeholder-gray-400 shadow-sm outline-doqit-primary
                        focus:outline-none bg-input-bg"
                            onChange={(e) => setConfirmDelete(e.target.value)}
                          />
                        </>
                      )}
                      <button
                        className={`w-full py-3 rounded-full font-medium text-white transition-colors
                        ${primaryButtonDisabled ? 'bg-gray-300 cursor-not-allowed' : ''}
                        ${!primaryButtonDisabled && isMyProfileModal ? 'bg-doqit-button-primary hover:opacity-90' : ''}
                        ${!primaryButtonDisabled && !isMyProfileModal ? 'bg-red-400 hover:bg-red-500' : ''}
                        ${isLaterModal ? 'reminder-later-btn reminder-btn' : ''}`}
                        type="button"
                        onClick={primaryButtonDisabled ? undefined : onHandlePrimaryClick}
                        data-cy="popup-primary-btn"
                        disabled={primaryButtonDisabled}
                      >
                        <span data-cy="primary-btn-text">
                          {primaryButtonText}
                        </span>
                      </button>
                      {secondaryButtonText && (
                        <button
                          className={`w-full py-2 font-medium transition-colors
                          ${(isDeleteAccountConfirmation && isConfirmedDelete !== 'DELETE') ? 'cursor-not-allowed text-gray-300' : ''}
                          ${isMyProfileModal ? 'cursor-pointer text-doqit-button-primary hover:opacity-80' : 'cursor-pointer text-red-500 hover:text-red-600'}
                          ${isLaterModal ? 'reminder-later-btn' : ''}`}
                          type="button"
                          onClick={onHandleSecondaryClick}
                          data-cy="popup-secondary-btn"
                          disabled={isDeleteAccountConfirmation && isConfirmedDelete !== 'DELETE'}
                        >
                          <span data-cy="secondary-btn-text">
                            {secondaryButtonText}
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default DialogBox;
